import React from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import { BRAND } from "./config";
import { getSiteBuilderProgress } from "./siteBuilderModel";

export default function SiteBuilderGuide({
  form,
  productsEnabled,
  activeStep,
  onSelectStep,
}) {
  const progress = React.useMemo(
    () => getSiteBuilderProgress(form, productsEnabled),
    [form, productsEnabled]
  );
  const nextIncompleteId = progress.steps.find((step) => !step.done)?.id || "";

  return (
    <Card
      elevation={0}
      sx={{
        position: { lg: "sticky" },
        top: { lg: 8 },
        alignSelf: "start",
        borderRadius: 4,
        overflow: "hidden",
        border: "1px solid rgba(25,36,47,.10)",
        boxShadow: "0 18px 50px rgba(25,36,47,.08)",
        bgcolor: "#fff",
      }}
    >
      <Box sx={{ p: 2.2, color: "#fff", bgcolor: BRAND.dark }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Box>
            <Typography variant="overline" sx={{ color: "rgba(255,255,255,.62)", fontWeight: 900, letterSpacing: 1.1 }}>
              RUTA DE PUBLICACIÓN
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 950, lineHeight: 1.1 }}>
              Diseñá tu web
            </Typography>
          </Box>
          <Chip
            size="small"
            label={`${progress.percentage}%`}
            sx={{ bgcolor: progress.complete ? "rgba(46,125,50,.24)" : "rgba(255,255,255,.12)", color: "#fff", fontWeight: 950 }}
          />
        </Stack>
        <Typography variant="body2" sx={{ mt: 1, color: "rgba(255,255,255,.72)", lineHeight: 1.45 }}>
          Son seis pasos concretos. Cuando lo esencial llega al 100%, la web queda lista para publicar.
        </Typography>
        <LinearProgress
          variant="determinate"
          value={progress.percentage}
          sx={{
            mt: 1.7,
            height: 8,
            borderRadius: 999,
            bgcolor: "rgba(255,255,255,.12)",
            "& .MuiLinearProgress-bar": { bgcolor: progress.complete ? "#52c878" : BRAND.orange, borderRadius: 999 },
          }}
        />
        <Typography variant="caption" sx={{ display: "block", mt: 0.7, color: "rgba(255,255,255,.60)", fontWeight: 750 }}>
          {progress.completed} de {progress.total} puntos esenciales listos
        </Typography>
      </Box>

      <Stack divider={<Divider flexItem />}>
        {progress.steps.map((step) => {
          const active = step.id === activeStep;
          const next = step.id === nextIncompleteId;
          return (
            <Button
              key={step.id}
              type="button"
              fullWidth
              onClick={() => onSelectStep(step.id)}
              sx={{
                px: 1.6,
                py: 1.35,
                borderRadius: 0,
                justifyContent: "flex-start",
                textAlign: "left",
                textTransform: "none",
                color: BRAND.dark,
                bgcolor: active ? "rgba(243,90,58,.075)" : "#fff",
                position: "relative",
                overflow: "hidden",
                "&:hover": { bgcolor: active ? "rgba(243,90,58,.11)" : "rgba(25,36,47,.035)" },
                ...(next && !active
                  ? {
                      "@keyframes builderStepPulse": {
                        "0%, 100%": { boxShadow: "inset 3px 0 0 rgba(243,90,58,.35)" },
                        "50%": { boxShadow: "inset 6px 0 0 rgba(243,90,58,.92)" },
                      },
                      animation: "builderStepPulse 1.8s ease-in-out infinite",
                    }
                  : {}),
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  flex: "0 0 auto",
                  display: "grid",
                  placeItems: "center",
                  borderRadius: "50%",
                  bgcolor: step.done ? "rgba(22,131,75,.11)" : active ? BRAND.orange : "rgba(25,36,47,.07)",
                  color: step.done ? "#16834b" : active ? "#fff" : "rgba(25,36,47,.64)",
                  fontWeight: 950,
                  fontSize: 13,
                }}
              >
                {step.done ? <CheckCircleRoundedIcon sx={{ fontSize: 20 }} /> : step.number}
              </Box>
              <Box sx={{ ml: 1.2, minWidth: 0, flex: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                  <Typography sx={{ fontWeight: 950, fontSize: ".92rem", lineHeight: 1.15 }}>
                    {step.shortLabel}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 900, color: step.done ? "#16834b" : "rgba(25,36,47,.48)" }}>
                    {step.percentage}%
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.7} alignItems="center" sx={{ mt: 0.4 }}>
                  {step.done ? (
                    <CheckCircleRoundedIcon sx={{ fontSize: 14, color: "#16834b" }} />
                  ) : (
                    <RadioButtonUncheckedRoundedIcon sx={{ fontSize: 14, color: "rgba(25,36,47,.30)" }} />
                  )}
                  <Typography variant="caption" sx={{ color: "rgba(25,36,47,.56)", lineHeight: 1.3 }}>
                    {step.done ? "Paso esencial completo" : `${step.completed}/${step.total} esenciales`}
                  </Typography>
                </Stack>
              </Box>
            </Button>
          );
        })}
      </Stack>

      <Box sx={{ p: 1.5, bgcolor: progress.complete ? "rgba(46,125,50,.055)" : "rgba(25,36,47,.025)" }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <AutoAwesomeRoundedIcon sx={{ color: progress.complete ? "#16834b" : BRAND.orange, fontSize: 20 }} />
          <Typography variant="caption" sx={{ color: "rgba(25,36,47,.66)", fontWeight: 800, lineHeight: 1.35 }}>
            {progress.complete
              ? "Círculo cerrado: lo esencial está completo."
              : "Las mejoras opcionales nunca bloquean la publicación."}
          </Typography>
        </Stack>
      </Box>
    </Card>
  );
}
